/**
 * Cookie utilities for authentication
 */

const PASSKEY_OPT_OUT_PREFIX = 'pk_opt_'
const TWO_WEEKS_IN_MS = 14 * 24 * 60 * 60 * 1000 // 14 days

/**
 * Sets a cookie value with security attributes
 */
function setCookie(name: string, value: string, expiresMs: number): void {
  const date = new Date()
  date.setTime(date.getTime() + expiresMs)
  const expires = `expires=${date.toUTCString()}`

  // Determine if we should use Secure flag based on current protocol
  // In production, cookies should always be secure
  // In development (http://localhost or http://*.lvh.me), we skip Secure flag
  const isSecureContext = window.location.protocol === 'https:'
  const secureFlag = isSecureContext ? ';Secure' : ''

  // Set cookie with security attributes:
  // - SameSite=Lax: Provides CSRF protection while allowing navigation
  // - Secure: Only send over HTTPS (when applicable)
  // - HttpOnly cannot be set via JavaScript (would need server-side setting)
  document.cookie = `${name}=${value};${expires};path=/;SameSite=Lax${secureFlag}`
}

/**
 * Gets a cookie value
 */
function getCookie(name: string): string | null {
  const nameEQ = `${name}=`
  const cookies = document.cookie.split(';')

  for (let cookie of cookies) {
    cookie = cookie.trim()
    if (cookie.indexOf(nameEQ) === 0) {
      return cookie.substring(nameEQ.length)
    }
  }

  return null
}

/**
 * Deletes a cookie with the same attributes used when setting
 */
function deleteCookie(name: string): void {
  const isSecureContext = window.location.protocol === 'https:'
  const secureFlag = isSecureContext ? ';Secure' : ''

  // Delete cookie with same attributes to ensure proper removal
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;SameSite=Lax${secureFlag}`
}

/**
 * Creates a cookie name for passkey opt-out using the provided storageId
 */
function getOptOutCookieName(storageId: string): string {
  return `${PASSKEY_OPT_OUT_PREFIX}${storageId}`
}

/**
 * Sets the passkey suggestion opt-out cookie for 2 weeks
 * @param storageId - The unique storage identifier from the backend
 */
export function setPasskeyOptOutCookie(storageId: string): void {
  const cookieName = getOptOutCookieName(storageId)
  setCookie(cookieName, 'true', TWO_WEEKS_IN_MS)
}

/**
 * Checks if passkey suggestions have been opted out for this storageId
 * @param storageId - The unique storage identifier from the backend
 */
export function hasOptedOutOfPasskeySuggestions(storageId: string): boolean {
  const cookieName = getOptOutCookieName(storageId)
  return getCookie(cookieName) === 'true'
}

/**
 * Clears the passkey opt-out cookie (if needed for testing or resetting)
 * @param storageId - The unique storage identifier from the backend
 */
export function clearPasskeyOptOutCookie(storageId: string): void {
  const cookieName = getOptOutCookieName(storageId)
  deleteCookie(cookieName)
}
